import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useSearchParams, Link } from "react-router-dom";
import { SubscriptionBreakdown } from "@/components/SubscriptionBreakdown";
import { RevisionBanner } from "@/components/chef/facturation/RevisionBanner";
import { PagInvoicesTable } from "@/components/chef/facturation/PagInvoicesTable";
import { CreditCard, Calendar, Clock, TrendingUp, AlertTriangle, XCircle, CheckCircle2, ArrowUpRight, Mail, Puzzle } from "lucide-react";

const STATUS_LABEL = {
  pending: "En attente",
  in_review: "En traitement",
  processed: "Résiliée",
  rejected: "Rejetée",
};
const STATUS_COLOR = {
  pending: "bg-[#FFD700] text-[#0A0A0A]",
  in_review: "bg-[#002FA7] text-white",
  processed: "bg-[#059669] text-white",
  rejected: "bg-[#E60000] text-white",
};

export default function ChefFacturation() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [params, setParams] = useSearchParams();
  const [highlight] = useState(() => (params.get("remind") === "1" ? params.get("pag_invoice") : null));

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/chef/facturation");
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Retour PayPal : capture d'une facture PAG (?pag_invoice=&paypal=return&token=) ou révision d'abonnement (?revision=return)
  useEffect(() => {
    const inv = params.get("pag_invoice"), pp = params.get("paypal"), token = params.get("token"), rev = params.get("revision");
    const st = params.get("stripe"), sess = params.get("session_id");
    if (params.get("remind") === "1") { setParams({}, { replace: true }); return; }
    if (!inv && !rev) return;
    (async () => {
      try {
        if (inv && st === "return" && sess) {
          const { data: r } = await api.post(`/chef/facturation/pag-invoice/${inv}/capture-stripe`, { session_id: sess });
          if (r.status === "paid") toast.success("Paiement par carte confirmé — facture réglée"); else toast.info("Paiement en cours de confirmation par Stripe");
        } else if (inv && st === "cancel") toast.info("Paiement par carte annulé");
        if (inv && pp === "return" && token) {
          const { data: r } = await api.post(`/chef/facturation/pag-invoice/${inv}/capture`, { order_id: token });
          if (r.status === "paid") toast.success("Paiement PayPal confirmé — facture réglée"); else toast.info("Paiement en cours de confirmation par PayPal");
        } else if (inv && pp === "cancel") toast.info("Paiement annulé");
        if (rev === "return") {
          const { data: r } = await api.post("/chef/facturation/revision/check");
          if (r.revision?.status === "approved") toast.success("Nouveau montant de prélèvement confirmé"); else if (r.revision) toast.info("PayPal n'a pas encore confirmé la révision");
        } else if (rev === "cancel") toast.info("Révision du prélèvement non approuvée");
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      setParams({}, { replace: true });
      load();
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submitCancel = async () => {
    if (reason.trim().length < 10) {
      toast.error("Motif obligatoire (min 10 caractères)");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/chef/facturation/cancel-request", { reason: reason.trim(), effective_date: effectiveDate || null });
      toast.success("Demande de résiliation envoyée à l'équipe PAG");
      setCancelOpen(false);
      setReason(""); setEffectiveDate("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const withdrawCancel = async (req) => {
    if (!confirm("Annuler cette demande de résiliation ?")) return;
    try {
      await api.delete(`/chef/facturation/cancel-request/${req.id}`);
      toast.success("Demande retirée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <div className="p-8 text-[#868E96]">Chargement…</div>;
  if (!data) return <div className="p-8 text-[#E60000]">Impossible de charger la facturation</div>;

  const ent = data.entreprise || {};
  const cycle = ent.last_payment_period === "monthly" ? "Mensuel" : "Annuel";
  const dueDate = ent.payment_due_date ? new Date(ent.payment_due_date).toLocaleDateString("fr-FR") : "—";
  const lastPaid = ent.last_paid_at ? new Date(ent.last_paid_at).toLocaleDateString("fr-FR") : "—";

  const nextRenewal = ent.payment_due_date ? (() => {
    const days = Math.ceil((new Date(ent.payment_due_date) - new Date()) / (1000 * 60 * 60 * 24));
    return days > 0 ? `Dans ${days} jours` : `${Math.abs(days)} jours de retard`;
  })() : "—";

  return (
    <div className="p-6 lg:p-10 max-w-6xl" data-testid="chef-facturation-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-[#868E96] font-bold mb-2">// Abonnement & facturation</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Ma facturation</h1>
        <p className="text-sm text-[#495057] mt-2">Retrouvez votre abonnement en cours, l&apos;historique de vos paiements, et gérez votre résiliation.</p>
      </div>

      {/* Alert if cancel request active */}
      {data.cancel_request && (
        <div data-testid="active-cancel-alert" className="mb-6 border-2 border-[#FFD700] bg-[#FFFDF2] p-5 flex items-start gap-4">
          <AlertTriangle className="h-6 w-6 text-[#FFD700] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-sm mb-1">Demande de résiliation en cours de traitement</div>
            <div className="text-xs text-[#495057] mb-2">Statut : <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_COLOR[data.cancel_request.status]}`}>{STATUS_LABEL[data.cancel_request.status]}</span></div>
            <div className="text-xs text-[#495057]">Motif : {data.cancel_request.reason}</div>
            {data.cancel_request.status === "pending" && (
              <Button variant="outline" size="sm" onClick={() => withdrawCancel(data.cancel_request)} data-testid="withdraw-cancel-btn" className="mt-3 h-8 text-xs">
                Retirer ma demande
              </Button>
            )}
            {data.cancel_request.admin_note && (
              <div className="mt-2 text-xs text-[#495057] italic">Note PAG : &laquo;&nbsp;{data.cancel_request.admin_note}&nbsp;&raquo;</div>
            )}
          </div>
        </div>
      )}

      {/* Subscription KPIs */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="subscription-kpis">
        <KPI icon={CreditCard} label="Plan actuel" value={(ent.plan || "business").toUpperCase()} sub={cycle} accent="#002FA7" />
        <KPI icon={Calendar} label="Dernier paiement" value={lastPaid} accent="#059669" />
        <KPI icon={Clock} label="Prochaine échéance" value={dueDate} sub={nextRenewal} accent="#FFD700" text="#0A0A0A" />
        <KPI icon={TrendingUp} label="Statut" value={ent.status === "active" ? "Actif" : ent.status === "warning" ? "Attention" : ent.status} accent={ent.status === "active" ? "#059669" : "#E60000"} />
      </div>

      {/* Actions */}
      <div className="flex gap-3 flex-wrap mb-8">
        <Link to="/chef/marketplace" data-testid="fact-add-module-link" className="inline-flex items-center h-11 px-5 bg-[#0A0A0A] text-white hover:bg-[#333] transition-colors font-semibold text-sm">
          <Puzzle className="h-4 w-4 mr-2" /> Ajouter un module sur mesure
        </Link>
        {!data.cancel_request && (
          <Button onClick={() => setCancelOpen(true)} data-testid="request-cancel-btn" variant="outline" className="h-11 border-2 border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white">
            <XCircle className="h-4 w-4 mr-2" /> Demander la résiliation
          </Button>
        )}
        <a href="mailto:contact@parcavenue.eu?subject=Facture demandée" className="inline-flex items-center h-11 px-5 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors font-semibold text-sm">
          <Mail className="h-4 w-4 mr-2" /> Demander une facture PDF
        </a>
      </div>

      {/* Révision du prélèvement (options) */}
      <RevisionBanner revision={data.revision} onRefresh={load} />

      {/* Abonnement détaillé : formule + modules + options */}
      <div className="mb-8">
        <SubscriptionBreakdown data={data.abonnement} />
      </div>

      {/* Factures PAG (lignes abonnement + options) */}
      <div className="mb-8">
        <PagInvoicesTable invoices={data.invoices} dueTotal={data.invoices_due_total} highlightId={highlight} providers={data.providers || { paypal: true, stripe: false }} />
      </div>

      {/* Payment history */}
      <div className="border border-[#DEE2E6]" data-testid="payments-history">
        <div className="p-5 border-b border-[#DEE2E6] bg-[#F8F9FA] flex items-center justify-between gap-4 flex-wrap">
          <h2 className="font-display text-2xl font-black tracking-tight">Historique des paiements</h2>
          <div className="flex items-center gap-2">
            {(() => {
              const years = [...new Set((data.payments || []).filter((p) => p.payment_status === "paid").map((p) => (p.created_at || "").slice(0, 4)))].filter(Boolean).sort().reverse();
              const currentLang = (localStorage.getItem("pag_language") || "fr").split("-")[0];
              return years.slice(0, 3).map((y) => (
                <a
                  key={y}
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/chef/facturation/invoices.zip?year=${y}&lang=${currentLang}`}
                  target="_blank" rel="noreferrer"
                  data-testid={`invoices-zip-${y}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold border border-[#0A0A0A] bg-white hover:bg-[#0A0A0A] hover:text-white transition-colors"
                  title={`Télécharger toutes les factures ${y} en ZIP`}
                >
                  📦 ZIP {y}
                </a>
              ));
            })()}
          </div>
        </div>
        {(data.payments || []).length === 0 ? (
          <div className="p-12 text-center text-[#868E96]" data-testid="payments-empty">Aucun paiement enregistré</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-[#868E96] font-bold">
              <tr className="border-b border-[#DEE2E6]">
                <th className="text-left px-5 py-3">Date</th>
                <th className="text-left px-5 py-3">Type</th>
                <th className="text-left px-5 py-3">Cycle</th>
                <th className="text-right px-5 py-3">Montant</th>
                <th className="text-left px-5 py-3">Statut</th>
                <th className="text-center px-3 py-3">Facture</th>
              </tr>
            </thead>
            <tbody>
              {(data.payments || []).map((p, i) => (
                <tr key={p.id || i} className="border-b border-[#DEE2E6]" data-testid={`payment-row-${i}`}>
                  <td className="px-5 py-3 font-mono text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                  <td className="px-5 py-3">
                    {p.kind === "renewal" ? "Renouvellement auto" : p.kind === "subscription" ? "Souscription" : p.kind || "—"}
                  </td>
                  <td className="px-5 py-3">{p.cycle === "annual" ? "Annuel" : p.cycle === "monthly" ? "Mensuel" : "—"}</td>
                  <td className="px-5 py-3 text-right font-semibold">{p.amount ? `${p.amount.toFixed(2)} €` : "—"}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${p.payment_status === "paid" ? "bg-[#059669] text-white" : "bg-[#DEE2E6] text-[#495057]"}`}>
                      {p.payment_status === "paid" ? "Payé" : p.payment_status === "pending" ? "En attente" : p.payment_status}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    {p.payment_status === "paid" && p.id && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/chef/facturation/invoice/${p.id}/pdf?lang=${(localStorage.getItem("pag_language") || "fr").split("-")[0]}`}
                        target="_blank" rel="noreferrer"
                        data-testid={`invoice-dl-${i}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors"
                        title="Télécharger la facture PDF (traduite dans la langue courante)"
                      >
                        📄 PDF
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Cancel dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-xl" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black">Demande de résiliation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="border-l-4 border-[#FFD700] bg-[#FFFDF2] p-4 text-sm">
              <div className="font-semibold mb-1">Traitement manuel par l&apos;équipe PAG</div>
              <p className="text-[#495057] text-xs">
                L&apos;annulation de votre abonnement Stripe est <b>traitée manuellement</b> par notre équipe. Vous conservez l&apos;accès jusqu&apos;à la fin de votre période payée.
                Vous recevrez une confirmation par email sous 48h ouvrées.
              </p>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Motif de la résiliation <span className="text-[#E60000]">*</span></Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder="Expliquez-nous pourquoi vous souhaitez résilier (min 10 caractères)…"
                data-testid="cancel-reason"
                className="mt-1"
              />
              <div className="text-[10px] text-[#868E96] mt-1">{reason.length}/500</div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Date souhaitée de résiliation (optionnel)</Label>
              <Input
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                data-testid="cancel-date"
                className="h-11 mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Annuler</Button>
            <Button
              onClick={submitCancel}
              disabled={submitting || reason.trim().length < 10}
              data-testid="cancel-submit"
              className="bg-[#E60000] hover:bg-[#B30000] text-white"
            >
              {submitting ? "Envoi…" : "Envoyer la demande"} <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, accent = "#0A0A0A", text = "white" }) {
  return (
    <div className="border border-[#DEE2E6] p-5" style={{ backgroundColor: accent, color: text }}>
      <Icon className="h-5 w-5 mb-3 opacity-70" />
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      <div className="font-display text-2xl font-black tracking-tighter mt-1">{value}</div>
      {sub && <div className="text-xs mt-1 opacity-80">{sub}</div>}
    </div>
  );
}
